<template>
  <div id="app" :class="{ 'tablet-mode': isTabletMode }">
    <template v-if="isLoggedIn">
      <!-- 收银台独立窗口：无侧栏 -->
      <div v-if="isCashierDeskMode" class="cashier-desk-root">
        <div class="cashier-desk-bar">
          <span>收银台</span>
          <span class="cashier-desk-expire">会话约 {{ cashierRemainText }} 后自动退出</span>
          <el-button type="text" class="cashier-desk-exit" @click="exitCashierDesk">退出收银台</el-button>
        </div>
        <router-view />
      </div>
      <el-container v-else class="layout-root">
        <el-aside :width="isTabletMode ? '104px' : '92px'" class="sidebar module-rail">
          <button
            type="button"
            class="rail-user"
            title="账号信息"
            @click="openAccountInfoDialog"
          >
            <div class="sidebar-user-avatar" :class="{ 'has-img': !!accountAvatarSrc }">
              <img v-if="accountAvatarSrc" :src="accountAvatarSrc" alt="" />
              <span v-else>{{ userInitial }}</span>
            </div>
            <span class="rail-user-name">{{ userInfo.displayName || userInfo.username || '管理员' }}</span>
          </button>
          <div class="rail-scroll">
            <button
              v-for="mod in visibleModules"
              :key="mod.id"
              type="button"
              class="rail-mod"
              :class="{ active: activeModuleId === mod.id }"
              @click="selectModule(mod)"
            >
              <i :class="mod.icon"></i>
              <span>{{ mod.label }}</span>
            </button>
          </div>
        </el-aside>
        <el-container class="content-column">
          <el-header class="topbar">
            <div class="topbar-inner">
              <div class="topbar-left">
                <div class="topbar-brand-mark">
                  <span class="topbar-brand-dot" aria-hidden="true"></span>
                  <span class="topbar-brand-name" v-show="!isTabletMode">温岭EATing创意融合轻食</span>
                </div>
                <h1 class="page-title" v-if="resolvedNav.module">{{ resolvedNav.module.label }}</h1>
              </div>
              <div class="topbar-right">
                <button
                  type="button"
                  class="ui-mode-toggle"
                  :class="{ 'is-tablet': isTabletMode }"
                  :aria-pressed="isTabletMode ? 'true' : 'false'"
                  title="切换网页 / 平板布局"
                  @click="onToggleUiMode"
                >
                  <span class="ui-mode-opt" :class="{ active: !isTabletMode }">网页</span>
                  <span class="ui-mode-opt" :class="{ active: isTabletMode }">平板</span>
                </button>
                <el-popover
                  placement="bottom"
                  width="320"
                  trigger="click"
                  v-model="notificationPopoverVisible"
                >
                  <div v-if="notifications.length" class="notification-panel">
                    <div class="notification-panel-head">
                      <span class="notification-panel-title">消息通知</span>
                      <el-button type="text" size="mini" class="notification-mark-all" @click="markAllNotificationsRead">
                        全部已读
                      </el-button>
                    </div>
                    <div
                      v-for="item in notifications"
                      :key="item._id"
                      class="notification-item"
                      :class="notificationItemClass(item)"
                      @click="handleNotificationClick(item)"
                    >
                      <div class="notification-item-icon">{{ notificationIcon(item) }}</div>
                      <div class="notification-item-body">
                        <div class="notification-item-title">{{ item.title }}</div>
                        <div class="notification-item-content">{{ item.content }}</div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="notification-empty">暂无新通知</div>
                  <el-badge :value="unreadCount" class="item" slot="reference">
                    <el-button type="text" icon="el-icon-bell"></el-button>
                  </el-badge>
                </el-popover>
                <el-badge :value="5" class="item">
                  <el-button type="text" icon="el-icon-chat-line-round"></el-button>
                </el-badge>
                <el-dropdown @command="handleUserCommand">
                  <span class="el-dropdown-link">
                    {{ userInfo.displayName || userInfo.username }} <i class="el-icon-arrow-down"></i>
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="personal">个人中心</el-dropdown-item>
                    <el-dropdown-item command="logout">退出登录</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </div>
          </el-header>
          <div class="task-bar" v-if="visibleTasks.length">
            <div class="task-pills">
              <button
                v-for="task in visibleTasks"
                :key="task.id"
                type="button"
                class="task-pill"
                :class="{ active: activeTaskId === task.id }"
                @click="selectTask(task)"
              >{{ task.label }}</button>
            </div>
          </div>
          <el-main style="padding: 0;">
            <div class="main-content">
              <router-view></router-view>
            </div>
          </el-main>
        </el-container>
      </el-container>

      <el-dialog
        :visible.sync="accountDialogVisible"
        width="480px"
        append-to-body
        custom-class="account-info-dialog"
        :show-close="true"
      >
        <div slot="title" class="account-dialog-title">
          <span class="account-dialog-title-main">账号信息</span>
          <span class="account-dialog-title-sub">资料与权限一览</span>
        </div>
        <div class="account-info-body" v-loading="accountSaving || accountAvatarUploading">
          <div class="account-hero">
            <el-upload
              class="account-avatar-upload"
              action="#"
              :show-file-list="false"
              :http-request="uploadAccountAvatar"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              :disabled="accountAvatarUploading"
            >
              <div class="account-avatar-ring">
                <img v-if="accountAvatarSrc" :src="accountAvatarSrc" class="account-avatar-img" alt="" />
                <span v-else class="account-avatar-fallback">{{ userInitial }}</span>
                <span class="account-avatar-mask"><i class="el-icon-camera"></i> 更换</span>
              </div>
            </el-upload>
            <div class="account-hero-meta">
              <div class="account-hero-name">{{ accountForm.name || userInfo.displayName || '—' }}</div>
              <div class="account-hero-role">{{ userInfo.roleLabel || '—' }}</div>
              <div class="account-hero-hint">点击头像可上传新图片（≤5MB）</div>
            </div>
          </div>

          <div class="account-field">
            <label class="account-field-label">姓名</label>
            <div class="account-field-control">
              <el-input
                v-model="accountForm.name"
                maxlength="50"
                show-word-limit
                placeholder="输入显示姓名"
                clearable
              />
            </div>
          </div>
          <div class="account-field account-field--readonly">
            <label class="account-field-label">登录账号</label>
            <div class="account-field-value">{{ userInfo.username || '—' }}</div>
          </div>
          <div class="account-field account-field--readonly">
            <label class="account-field-label">员工 ID</label>
            <div class="account-field-value">{{ userInfo.employeeId != null && userInfo.employeeId !== '' ? userInfo.employeeId : '—' }}</div>
          </div>
          <div class="account-field account-field--readonly">
            <label class="account-field-label">角色</label>
            <div class="account-field-value">{{ userInfo.roleLabel || '—' }}</div>
          </div>
          <div class="account-field account-field--block">
            <label class="account-field-label">功能权限</label>
            <div v-if="userInfo.permissions && userInfo.permissions.length" class="account-info-perms">
              <el-tag
                v-for="p in userInfo.permissions"
                :key="p"
                size="small"
                effect="plain"
                class="account-info-tag"
              >{{ p }}</el-tag>
            </div>
            <div v-else class="account-field-value">—</div>
          </div>
          <div class="account-field account-field--readonly">
            <label class="account-field-label">登录时间</label>
            <div class="account-field-value">{{ formatAccountLoginTime }}</div>
          </div>
        </div>
        <span slot="footer" class="dialog-footer account-dialog-footer">
          <el-button @click="accountDialogVisible = false">取消</el-button>
          <el-button type="primary" plain @click="goToPersonalCenterFromAccount">个人中心</el-button>
          <el-button type="primary" :loading="accountSaving" @click="saveAccountProfile">保存姓名</el-button>
        </span>
      </el-dialog>
    </template>
    <template v-else>
      <router-view></router-view>
    </template>
  </div>
</template>

<script>
import { clearAuthSession, getAdminToken, getIsLoggedIn, getUserInfo, patchUserInfo } from './utils/auth'
import {
  clearCashierDeskSession,
  cashierDeskRemainMs,
  isCashierDeskSessionValid
} from './utils/cashierSession'
import { uiMode, toggleTabletMode } from './utils/uiMode'
import { filterModules, resolveNav } from './utils/navModules'
import { apiBase } from './api/client'
import appConfirm from './utils/appConfirm'

export default {
  name: 'App',
  data() {
    return {
      subMenu: 'order',
      userInfo: {},
      loggedIn: getIsLoggedIn() && !!(getAdminToken() && String(getAdminToken()).trim()),
      notifications: [],
      unreadCount: 0,
      notificationPopoverVisible: false,
      notificationTimer: null,
      accountDialogVisible: false,
      accountForm: { name: '' },
      accountSaving: false,
      accountAvatarUploading: false,
      cashierRemainTick: Date.now(),
      cashierRemainTimer: null
    }
  },
  computed: {
    isTabletMode() {
      return !!uiMode.isTabletMode
    },
    accountAvatarSrc() {
      const raw = (this.userInfo && (this.userInfo.avatarUrl || this.userInfo.avatar_url)) || ''
      return this.resolveMediaUrl(raw)
    },
    navCtx() {
      return {
        hasMenuPerm: (perm) => this.hasMenuPerm(perm),
        canMenuCashier: this.canMenuCashier(),
        isStaffCashier: this.isStaffCashierSession,
        canReviewDelete: this.canReviewDeleteRequestsSession
      }
    },
    visibleModules() {
      return filterModules(this.navCtx)
    },
    resolvedNav() {
      return resolveNav(this.$route, this.visibleModules)
    },
    activeModuleId() {
      return (this.resolvedNav.module && this.resolvedNav.module.id) || ''
    },
    activeTaskId() {
      return (this.resolvedNav.task && this.resolvedNav.task.id) || ''
    },
    visibleTasks() {
      const mod = this.resolvedNav.module
      return (mod && mod.tasks) || []
    },
    activeIndex() {
      return this.$route.path;
    },
    isCashierDeskMode() {
      return this.$route.path === '/cashier-desk'
    },
    cashierRemainText() {
      void this.cashierRemainTick
      const ms = cashierDeskRemainMs()
      if (ms <= 0) return '已到期'
      const h = Math.floor(ms / 3600000)
      const m = Math.floor((ms % 3600000) / 60000)
      if (h > 0) return `${h} 小时 ${m} 分`
      return `${Math.max(1, m)} 分钟`
    },
    pageTitle() {
      if (this.resolvedNav.task && this.resolvedNav.task.label) {
        return this.resolvedNav.task.label
      }
      const titles = {
        '/': '工作台',
        '/employees': '员工管理',
        '/store-attendance': '门店考勤',
        '/attendance-calendar': '出勤日历',
        '/operation-logs': '操作日志',
        '/delete-requests-review': '审核申请',
        '/products': '商品管理',
        '/stores': '店铺管理',
        '/display-config': '取餐大屏',
        '/groupon-settings': '团购核销对接',
        '/groupon-verify': '团购扫码核销',
        '/orders': '线上订单',
        '/stores': '店铺管理',
        '/orders/situation': '收银台订单',
        '/cashier': '进入收银台',
        '/cashier-desk': '收银台',
        '/meal-holiday-config': '随心订排餐',
        '/subscription-calendar': '随心订排餐',
        '/subscriptions': '套餐计划',
        '/mini-decoration': '小程序设置',
        '/banner-management': '轮播图管理',
        '/portal-site': '官网门户',
        '/vouchers': '兑换券管理',
        '/inventory/dashboard': '库存统计仪表盘',
        '/inventory/inbound-logs': '入库日志统计',
        '/inventory/outbound-logs': '出库日志统计',
        '/inventory/inout-report': '出入库填报',
        '/member/level-rules': '等级规则',
        '/member/logs': '等级变动日志',
        '/member/points-mall': '积分商城',
        '/riders/info': '骑手个人信息',
        '/riders/today': '今日配送',
        '/riders/map': '骑手实时地图',
        '/receipt/template': '映美/飞鹅模板',
        '/receipt/print': '映美/飞鹅打印',
        '/users': '客户查询',
        '/user-accounts': '用户管理',
        '/expiry-management': '效期管理',
        '/deferral-query': '延期查询',
        '/personal': '个人中心',
        '/products/create': '创建商品',
        '/email-batch': '邮件批量发送'
      };
      return titles[this.activeIndex] || '管理系统';
    },
    documentPageTitle() {
      const site = '温岭迅驰餐饮管理系统'
      if (this.isCashierDeskMode || (this.$route && this.$route.path === '/cashier-desk')) {
        return `${site}-收银台`
      }
      return site
    },
    isLoggedIn() {
      return this.loggedIn;
    },
    userInitial() {
      const n = (this.userInfo && (this.userInfo.displayName || this.userInfo.username)) || '管'
      return String(n).trim().slice(0, 1) || '管'
    },
    isAdminUser() {
      const u = this.userInfo
      return !!(
        u &&
        (u.role === 'admin' ||
          u.roleLabel === '管理员' ||
          u.roleLabel === '超级管理员')
      )
    },
    isStaffCashierSession() {
      const r = String((this.userInfo && this.userInfo.roleLabel) || '').trim()
      return r === '员工' || r === '收银员'
    },
    canReviewDeleteRequestsSession() {
      const r = String((this.userInfo && this.userInfo.roleLabel) || '').trim()
      return r === '管理员' || r === '超级管理员'
    },
    formatAccountLoginTime() {
      const t = this.userInfo && this.userInfo.loginTime
      if (!t) return '—'
      const d = new Date(t)
      if (Number.isNaN(d.getTime())) return String(t).slice(0, 19).replace('T', ' ')
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    }
  },
  mounted() {
    this.getUserInfo();
    // 未登录（如登录页）不请求通知接口，避免无意义 502 / 控制台报错
    this.syncNotificationPolling();
    this.syncCashierDeskTimer();
    this.syncDocumentTitle();
  },
  beforeDestroy() {
    this.stopNotificationPolling();
    this.stopCashierDeskTimer();
  },
  watch: {
    documentPageTitle: {
      handler() {
        this.syncDocumentTitle();
      },
      immediate: true
    },
    '$route': {
      handler(to) {
        this.loggedIn = getIsLoggedIn() && !!(getAdminToken() && String(getAdminToken()).trim())
        this.getUserInfo();
        this.syncNotificationPolling();
        this.syncCashierDeskTimer();
        this.syncDocumentTitle();
        if (to && to.path === '/orders') {
          const tab = to.query && to.query.tab ? String(to.query.tab) : 'order'
          this.subMenu = tab
        }
      },
      immediate: true
    }
  },
  methods: {
    syncDocumentTitle() {
      if (typeof document === 'undefined') return
      document.title = this.documentPageTitle || '温岭迅驰餐饮管理系统'
    },
    syncCashierDeskTimer() {
      this.stopCashierDeskTimer()
      if (!this.isCashierDeskMode) return
      this.cashierRemainTick = Date.now()
      this.cashierRemainTimer = setInterval(() => {
        this.cashierRemainTick = Date.now()
        if (!isCashierDeskSessionValid()) {
          this.exitCashierDesk('收银台会话已满 24 小时，请重新进入')
        }
      }, 30000)
    },
    stopCashierDeskTimer() {
      if (this.cashierRemainTimer) {
        clearInterval(this.cashierRemainTimer)
        this.cashierRemainTimer = null
      }
    },
    exitCashierDesk(msg) {
      clearCashierDeskSession()
      this.stopCashierDeskTimer()
      if (msg) this.$message.warning(String(msg))
      if (window.opener) {
        window.close()
        this.$router.replace('/cashier').catch(() => {})
      } else {
        this.$router.replace('/cashier').catch(() => {})
      }
    },
    selectModule(mod) {
      if (!mod || !mod.tasks || !mod.tasks.length) return
      this.navigateToTask(mod.tasks[0])
    },
    selectTask(task) {
      this.navigateToTask(task)
    },
    navigateToTask(task) {
      if (!task || !task.path) return
      const query = task.query ? Object.assign({}, task.query) : {}
      this.$router.push({ path: task.path, query }).catch(() => {})
    },
    async onToggleUiMode() {
      try {
        await appConfirm({
          title: '提示',
          message: '确定切换网页版或者平板版',
          type: 'warning',
          confirmText: '确定',
          cancelText: '取消'
        })
      } catch (_e) {
        return
      }
      toggleTabletMode()
      this.$nextTick(() => {
        try {
          window.dispatchEvent(new Event('resize'))
        } catch (_e) {}
      })
    },
    stopNotificationPolling() {
      if (this.notificationTimer) {
        clearInterval(this.notificationTimer);
        this.notificationTimer = null;
      }
    },
    /** 仅登录后轮询未读通知；登出或停留在登录页时不请求 */
    syncNotificationPolling() {
      this.stopNotificationPolling();
      if (!getIsLoggedIn()) {
        this.notifications = [];
        this.unreadCount = 0;
        return;
      }
      this.fetchNotifications();
      this.notificationTimer = setInterval(() => {
        this.fetchNotifications(true);
      }, 15000);
    },
    async fetchNotifications(isAuto = false) {
      try {
        const resp = await this.$api.get('/admin/notifications/unread');
        if (resp && resp.code === 200) {
          let list = Array.isArray(resp.data) ? resp.data.slice() : [];
          const reviewNotices = await this.fetchPendingDeleteRequestNotices();
          if (reviewNotices.length) {
            const normalNotices = list.filter((x) => String((x && x.type) || '').trim() !== 'delete_request_review')
            list = [...reviewNotices, ...normalNotices]
          }
          if (isAuto && list.length > this.unreadCount && list[0]) {
            const n0 = list[0]
            this.$notify({
              title: n0.title || '随心订变更提醒',
              message: n0.content || '',
              position: 'top-right',
              type: this.notificationNotifyType(n0),
              duration: 4500
            });
          }
          this.notifications = list;
          this.unreadCount = list.length;
        }
      } catch (e) {
        // 静默失败即可
      }
    },
    deleteRequestTargetTypeLabel(t) {
      const x = String(t || '').trim()
      if (x === 'order') return '订单删除'
      if (x === 'order_refund') return '退款申请'
      if (x === 'order_cancel') return '取消订单申请'
      if (x === 'subscription_refund') return '随心订退款申请'
      if (x === 'subscription_cancel') return '随心订取消申请'
      if (x === 'voucher') return '兑换券删除'
      if (x === 'points_goods') return '积分商品删除'
      return x || '未知类型'
    },
    async fetchPendingDeleteRequestNotices() {
      if (!this.canReviewDeleteRequestsSession) return []
      try {
        const resp = await this.$api.get('/admin/delete-requests', { params: { status: 'pending' } })
        if (!resp || resp.code !== 200 || !Array.isArray(resp.data)) return []
        return resp.data.map((row) => {
          const id = Number(row && row.id)
          const target = row && row.target_label ? row.target_label : (row && row.target_id ? row.target_id : '-')
          const targetType = this.deleteRequestTargetTypeLabel(row && row.target_type)
          const reason = row && row.reason ? String(row.reason).trim() : ''
          const applicant = row && row.requested_by_name ? row.requested_by_name : '—'
          return {
            _id: `delete-request-${id}`,
            type: 'delete_request_review',
            title: `${targetType}待审核（申请ID：${id}）`,
            content: `申请人：${applicant}；目标：${target}${reason ? `；原因：${reason}` : ''}`,
            request_id: id
          }
        })
      } catch (_e) {
        return []
      }
    },
    notificationNotifyType(item) {
      const t = String((item && item.type) || '').trim()
      if (t === 'rider_delivery_complete') return 'success'
      if (t === 'delete_request_review') return 'warning'
      if (t === 'subscription_delay') return 'warning'
      return 'info'
    },
    notificationIcon(item) {
      const t = String((item && item.type) || '').trim()
      if (t === 'rider_delivery_complete') return '✓'
      if (t === 'delete_request_review') return '!'
      if (t === 'subscription_delay') return '延'
      return '•'
    },
    notificationItemClass(item) {
      const t = String((item && item.type) || '').trim()
      if (t === 'rider_delivery_complete') return 'is-delivery-done'
      if (t === 'delete_request_review') return 'is-review'
      if (t === 'subscription_delay') return 'is-review'
      return 'is-info'
    },
    async markAllNotificationsRead() {
      try {
        await this.$api.post('/admin/notifications/mark-all-read');
        await this.fetchNotifications();
        this.$message.success('通知已清空');
      } catch (e) {
        this.$message.error('操作失败');
      }
    },
    parseDeleteRequestIdFromNotification(item) {
      const type = String((item && item.type) || '').trim()
      if (type !== 'delete_request_review') return null
      const rid = Number(item && item.request_id)
      if (Number.isFinite(rid) && rid > 0) return rid
      const text = `${item && item.title ? item.title : ''} ${item && item.content ? item.content : ''}`
      const m = text.match(/申请ID[:：]\s*(\d+)/)
      if (!m) return null
      const n = Number(m[1])
      return Number.isFinite(n) ? n : null
    },
    async handleNotificationClick(item) {
      const type = String((item && item.type) || '').trim()
      if (type === 'backup_review_reminder') {
        this.notificationPopoverVisible = false
        try {
          await this.$router.push({ path: '/delete-requests-review' })
        } catch (_e) {
          /* ignore navigation duplicated */
        }
        return
      }
      const reqId = this.parseDeleteRequestIdFromNotification(item)
      if (!reqId) return
      this.notificationPopoverVisible = false
      try {
        await this.$router.push({ path: '/delete-requests-review', query: { deleteRequestId: String(reqId) } })
      } catch (_e) {
        /* ignore navigation duplicated */
      }
    },
    changeOrderTab(tab) {
      this.subMenu = tab
      const query = { ...(this.$route.query || {}), tab }
      if (this.$route.path !== '/orders') {
        this.$router.push({ path: '/orders', query })
      } else {
        this.$router.replace({ path: '/orders', query }).catch(() => {})
      }
    },
    handleUserCommand(cmd) {
      if (cmd === 'personal') return this.goToPersonalCenter()
      if (cmd === 'logout') return this.handleLogout()
    },
    getUserInfo() {
      this.userInfo = getUserInfo()
    },
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        clearAuthSession()
        this.loggedIn = false
        this.userInfo = {};
        this.stopNotificationPolling();
        this.notifications = [];
        this.unreadCount = 0;

        // Hash 路由下最稳的方式：先用 router，再兜底直接改 hash
        this.$router.replace('/login').catch(() => {});
        this.$nextTick(() => {
          if (window.location.hash !== '#/login') {
            window.location.hash = '#/login'
          }
        })
        
        // 显示退出成功消息
        this.$message.success('退出登录成功！');
      }).catch(() => {
      });
    },
    goToPersonalCenter() {
      this.$router.push('/personal');
    },
    openAccountInfoDialog() {
      this.getUserInfo()
      this.accountForm = {
        name: String((this.userInfo && (this.userInfo.displayName || this.userInfo.username)) || '')
      }
      this.accountDialogVisible = true
      this.refreshAccountProfile()
    },
    resolveMediaUrl(url) {
      const s = String(url || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s) || s.startsWith('data:')) return s
      const origin = String(apiBase || '').replace(/\/api\/?$/, '')
      if (s.startsWith('/')) return origin + s
      return origin + '/' + s.replace(/^\.\//, '')
    },
    applyProfileToSession(data) {
      if (!data || typeof data !== 'object') return
      const patch = {}
      if (data.name != null) patch.displayName = String(data.name)
      if (data.avatar_url != null) patch.avatarUrl = String(data.avatar_url || '')
      if (data.username) patch.username = String(data.username)
      if (data.employeeId != null && data.employeeId !== '') patch.employeeId = data.employeeId
      if (data.role) patch.roleLabel = String(data.role)
      if (Array.isArray(data.permissions)) patch.permissions = data.permissions
      this.userInfo = patchUserInfo(patch)
      if (data.name != null) {
        this.accountForm.name = String(data.name)
      }
    },
    async refreshAccountProfile() {
      try {
        const resp = await this.$api.get('/admin/profile')
        if (resp && resp.code === 200 && resp.data) {
          this.applyProfileToSession(resp.data)
        }
      } catch (_e) {
        // 弹窗仍可用本地会话数据
      }
    },
    async saveAccountProfile() {
      const name = String((this.accountForm && this.accountForm.name) || '').trim()
      if (!name) {
        this.$message.warning('请输入姓名')
        return
      }
      this.accountSaving = true
      try {
        const resp = await this.$api.put('/admin/profile', { name })
        if (resp && resp.code === 200 && resp.data) {
          this.applyProfileToSession(resp.data)
          this.$message.success((resp && resp.message) || '姓名已保存')
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        const msg =
          (e && e.apiBody && e.apiBody.message) ||
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '保存失败'
        this.$message.error(String(msg))
      } finally {
        this.accountSaving = false
      }
    },
    async uploadAccountAvatar(req) {
      const file = req && req.file
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片不能超过 5MB')
        return
      }
      const fd = new FormData()
      fd.append('file', file)
      this.accountAvatarUploading = true
      try {
        const resp = await this.$api.post('/admin/profile/avatar', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        })
        if (resp && resp.code === 200 && resp.data) {
          this.applyProfileToSession(resp.data)
          this.$message.success((resp && resp.message) || '头像已更新')
        } else {
          this.$message.error((resp && resp.message) || '上传失败')
        }
      } catch (e) {
        const msg =
          (e && e.apiBody && e.apiBody.message) ||
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '上传失败'
        this.$message.error(String(msg))
      } finally {
        this.accountAvatarUploading = false
      }
    },
    goToPersonalCenterFromAccount() {
      this.accountDialogVisible = false
      this.goToPersonalCenter()
    },
    hasMenuPerm(perm) {
      if (this.isAdminUser) return true
      const u = this.userInfo || {}
      return Array.isArray(u.permissions) && u.permissions.includes(perm)
    },
    canMenuCashier() {
      if (this.isAdminUser) return true
      const u = this.userInfo || {}
      if (u.roleLabel === '收银员') return true
      return this.hasMenuPerm('订单管理')
    }
  }
}
</script>

<style>
.layout-root {
  height: 100vh;
  min-height: 100vh;
  background: #0c1118;
  overflow: hidden;
}

.layout-root > .el-container,
.layout-root > .content-column {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.content-column {
  display: flex;
  flex-direction: column;
  background: #eef2f7;
}

.sidebar {
  background:
    radial-gradient(120% 80% at 0% 0%, rgba(43, 182, 115, 0.12), transparent 45%),
    linear-gradient(180deg, #141b24 0%, #0c1118 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.92);
  box-shadow: 8px 0 32px rgba(0, 0, 0, 0.22);
}

.module-rail {
  overflow: hidden;
  height: 100vh;
}

.module-rail .sidebar-user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
}

.rail-user {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 18px 8px 14px;
  border: 0;
  background: rgba(0, 0, 0, 0.18);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  cursor: pointer;
  color: #fff;
  font-family: inherit;
  flex-shrink: 0;
}

.rail-user-name {
  max-width: 76px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rail-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 10px 8px 16px;
  min-height: 0;
}

.rail-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.rail-mod {
  width: 100%;
  min-height: 72px;
  margin: 0 0 8px;
  padding: 10px 4px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: inherit;
  transition: background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
}

.rail-mod i {
  font-size: 24px;
}

.rail-mod span {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
}

.rail-mod:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.rail-mod.active {
  background: linear-gradient(180deg, #2bb673 0%, #1f9a5f 100%);
  color: #fff;
  box-shadow: 0 8px 18px rgba(43, 182, 115, 0.28);
}

.task-bar {
  flex-shrink: 0;
  background: #fff;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  padding: 10px 22px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.task-pills {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  min-width: 0;
  overflow-x: auto;
  line-height: 1;
}

.task-pills::-webkit-scrollbar {
  height: 0;
}

.task-pill {
  height: 36px;
  line-height: 34px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.task-pill:hover {
  color: #0f172a;
  border-color: #cbd5e1;
  background: #f8fafc;
}

.task-pill.active {
  background: linear-gradient(135deg, #22a06b 0%, #2bb673 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 4px 12px rgba(43, 182, 115, 0.22);
}

.logo {
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(0, 0, 0, 0.22);
  flex-shrink: 0;
}

.logo-text {
  letter-spacing: 0.03em;
  color: #ffffff;
  white-space: normal;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.25;
  max-width: 100%;
}

.logo-text-line1 {
  font-size: clamp(13px, 1.6vw, 15px);
  font-weight: 700;
  white-space: nowrap;
  font-family: var(--app-font);
}

.logo-text-line2 {
  font-size: clamp(10px, 1.2vw, 12px);
  font-weight: 500;
  opacity: 0.72;
  white-space: nowrap;
}

.sidebar-scroll {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.sidebar-menu {
  border-right: none !important;
  background-color: transparent !important;
  padding: 12px 8px 8px;
  flex: 1;
  overflow-y: auto;
}

.sidebar-menu ::v-deep(.el-menu-item),
.sidebar-menu ::v-deep(.el-submenu__title) {
  height: auto;
  line-height: 1.45;
  padding: 11px 14px !important;
  margin: 3px 6px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  transition: background-color 0.22s ease, color 0.22s ease, box-shadow 0.22s ease;
  color: rgba(255, 255, 255, 0.9) !important;
  background-color: transparent !important;
  background-image: none !important;
}

.sidebar-menu ::v-deep(.el-menu-item i),
.sidebar-menu ::v-deep(.el-submenu__title i) {
  font-size: 18px;
  margin-right: 10px;
  color: rgba(255, 255, 255, 0.82);
}

.sidebar-menu ::v-deep(.el-menu-item span),
.sidebar-menu ::v-deep(.el-submenu__title span) {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.sidebar-menu ::v-deep(.el-menu-item:hover),
.sidebar-menu ::v-deep(.el-submenu__title:hover),
.sidebar-menu ::v-deep(.el-menu-item:focus),
.sidebar-menu ::v-deep(.el-submenu__title:focus) {
  background-color: rgba(255, 255, 255, 0.08) !important;
  background-image: none !important;
  outline: none;
}

.sidebar-menu ::v-deep(.el-menu-item.is-disabled),
.sidebar-menu ::v-deep(.el-submenu__title.is-disabled),
.sidebar-menu ::v-deep(.el-menu-item.is-disabled i),
.sidebar-menu ::v-deep(.el-menu-item.is-disabled span),
.sidebar-menu ::v-deep(.el-submenu__title.is-disabled i),
.sidebar-menu ::v-deep(.el-submenu__title.is-disabled span) {
  color: rgba(255, 255, 255, 0.9) !important;
  opacity: 1 !important;
  background-color: transparent !important;
}

.sidebar-menu ::v-deep(.el-menu-item.is-active),
.sidebar-menu ::v-deep(.el-menu-item.is-active:hover),
.sidebar-menu ::v-deep(.el-menu-item.is-active:focus) {
  background-color: transparent !important;
  background-image: linear-gradient(90deg, rgba(43, 182, 115, 0.24), rgba(255, 255, 255, 0.05)) !important;
  position: relative;
  box-shadow: inset 0 0 0 1px rgba(43, 182, 115, 0.22);
}

.sidebar-menu ::v-deep(.el-menu-item.is-active::before) {
  content: '';
  position: absolute;
  left: 4px;
  top: 10px;
  bottom: 10px;
  width: 3px;
  border-radius: 999px;
  background: linear-gradient(180deg, #4ade80, #2bb673);
  box-shadow: 0 0 10px rgba(43, 182, 115, 0.55);
}

.sidebar-menu ::v-deep(.el-menu-item.is-active i),
.sidebar-menu ::v-deep(.el-menu-item.is-active span) {
  color: #ffffff !important;
  font-weight: 600;
}

.sidebar-menu ::v-deep(.el-menu),
.sidebar-menu ::v-deep(.el-submenu .el-menu) {
  background-color: transparent !important;
}

.sidebar-menu ::v-deep(.el-submenu.is-opened > .el-submenu__title),
.sidebar-menu ::v-deep(.el-submenu.is-active > .el-submenu__title) {
  background-color: rgba(255, 255, 255, 0.06) !important;
  background-image: none !important;
}

.sidebar-menu ::v-deep(.el-menu--inline) {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  margin: 4px 8px 8px;
  padding: 4px 0;
}

.sidebar-menu ::v-deep(.el-submenu .el-menu-item) {
  margin: 2px 8px;
  min-width: auto;
  background-color: transparent !important;
  background-image: none !important;
}

.sidebar-menu ::v-deep(.el-submenu .el-menu-item:hover),
.sidebar-menu ::v-deep(.el-submenu .el-menu-item:focus) {
  background-color: rgba(255, 255, 255, 0.08) !important;
  background-image: none !important;
}

.sidebar-menu ::v-deep(.el-submenu .el-menu-item.is-active),
.sidebar-menu ::v-deep(.el-submenu .el-menu-item.is-active:hover),
.sidebar-menu ::v-deep(.el-submenu .el-menu-item.is-active:focus) {
  background-color: transparent !important;
  background-image: linear-gradient(90deg, rgba(43, 182, 115, 0.22), rgba(255, 255, 255, 0.04)) !important;
}

.el-menu--popup {
  background: #141b24 !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.el-menu--popup .el-menu-item,
.el-menu--popup .el-submenu__title {
  color: #ffffff !important;
  background: transparent !important;
}

.el-menu--popup .el-menu-item i,
.el-menu--popup .el-submenu__title i,
.el-menu--popup .el-menu-item span,
.el-menu--popup .el-submenu__title span {
  color: #ffffff !important;
}

.el-menu--popup .el-menu-item:hover,
.el-menu--popup .el-submenu__title:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.el-menu--popup .el-menu-item.is-active {
  background: rgba(43, 182, 115, 0.18) !important;
}

.sidebar-section-title {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.42);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 10px 18px 4px;
  margin-top: 6px;
}

.sidebar-section-divider {
  height: 1px;
  margin: 10px 14px 6px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.14), transparent);
}

.sidebar-footer {
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  padding-top: 10px;
  background: rgba(0, 0, 0, 0.18);
}

.sidebar-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px 12px;
}

.sidebar-user--collapsed {
  justify-content: center;
  padding-bottom: 14px;
}

.sidebar-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2bb673, #0f172a);
  border: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 6px 14px rgba(43, 182, 115, 0.28);
  overflow: hidden;
}

.sidebar-user-avatar.has-img {
  background: #0f172a;
  padding: 0;
}

.sidebar-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.sidebar-user-meta {
  min-width: 0;
}

.sidebar-user-name {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-role {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user--clickable {
  cursor: pointer;
  border-radius: 12px;
  margin: 0 8px 4px;
  padding: 8px 8px 12px;
  transition: background-color 0.22s ease, transform 0.22s ease;
  outline: none;
}

.sidebar-user--clickable:hover {
  background: rgba(255, 255, 255, 0.07);
}

.sidebar-user--clickable:focus-visible {
  box-shadow: 0 0 0 2px rgba(43, 182, 115, 0.45);
}

.sidebar-user--collapsed.sidebar-user--clickable {
  margin: 0 auto 8px;
  padding: 8px;
  width: fit-content;
}

.sidebar-footer-text {
  padding: 0 16px 14px;
  font-size: 11px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.45);
}

.beian-inline {
  margin-bottom: 4px;
}

.topbar {
  background: rgba(12, 17, 24, 0.88) !important;
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  padding: 0 22px;
  height: 60px !important;
  line-height: 60px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.topbar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.topbar-icon-btn {
  color: rgba(255, 255, 255, 0.88) !important;
  font-size: 20px;
  padding: 8px !important;
  border-radius: 10px !important;
}

.topbar-icon-btn:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.08) !important;
}

.topbar-brand-mark {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 8px 0 4px;
  flex-shrink: 0;
}

.topbar-brand-dot {
  width: 10px;
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(135deg, #4ade80, #2bb673);
  box-shadow: 0 0 12px rgba(43, 182, 115, 0.55);
}

.topbar-brand-name {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.01em;
  white-space: nowrap;
  font-family: var(--app-font);
}

.page-title {
  margin: 0 0 0 12px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: var(--app-font);
}

.top-tabs {
  display: flex;
  gap: 6px;
  margin-left: 20px;
  align-items: center;
  padding: 4px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.top-tabs .el-button--text {
  color: rgba(255, 255, 255, 0.55) !important;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 12px !important;
  position: relative;
  border-radius: 9px !important;
}

.top-tabs .el-button--text:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.06) !important;
}

.top-tabs .el-button--text.active {
  color: #ffffff !important;
  font-weight: 700;
  background: rgba(43, 182, 115, 0.18) !important;
}

.top-tabs .el-button--text.active::after {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 3px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, #4ade80, #2bb673);
}

.ui-mode-toggle {
  display: inline-flex;
  align-items: center;
  padding: 3px;
  margin-right: 10px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  flex-shrink: 0;
  min-height: 36px;
}
.ui-mode-toggle .ui-mode-opt {
  min-width: 44px;
  height: 28px;
  line-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  transition: background 0.2s ease, color 0.2s ease;
}
.ui-mode-toggle .ui-mode-opt.active {
  background: linear-gradient(135deg, #22a06b 0%, #2bb673 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(43, 182, 115, 0.28);
}
.ui-mode-toggle.is-tablet {
  border-color: rgba(43, 182, 115, 0.35);
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
}

.topbar-right .el-popover__reference-wrapper {
  display: inline-flex;
  align-items: center;
  line-height: 1;
  vertical-align: middle;
}

.topbar-right .el-button--text {
  color: rgba(255, 255, 255, 0.85) !important;
  border-radius: 10px !important;
}

.topbar-right .el-button--text:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.08) !important;
}

.topbar-right .el-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  vertical-align: middle;
}

.topbar-right .el-badge .el-button--text {
  padding: 6px 8px !important;
  font-size: 18px;
  line-height: 1;
}

.topbar-right .el-badge .el-button--text [class^='el-icon-'] {
  vertical-align: middle;
}

.topbar-right .el-badge__content.is-fixed {
  top: 6px !important;
  right: 4px !important;
  transform: translate(50%, -50%) !important;
}

.el-dropdown-link {
  cursor: pointer;
  color: rgba(255, 255, 255, 0.92) !important;
  font-size: 14px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 10px;
  transition: background-color 0.2s ease;
}

.el-dropdown-link:hover {
  background: rgba(255, 255, 255, 0.08);
}

.item {
  margin-top: 0;
}

.main-content {
  padding: 22px 26px 32px;
  background:
    radial-gradient(900px 420px at 8% -10%, rgba(43, 182, 115, 0.1), transparent 55%),
    radial-gradient(700px 380px at 92% 0%, rgba(15, 23, 42, 0.06), transparent 50%),
    linear-gradient(180deg, #eef2f7 0%, #e8edf5 100%);
  min-height: 100%;
  box-sizing: border-box;
}

.el-main {
  overflow-y: auto;
  background: #eef2f7;
  flex: 1;
  min-height: 0;
}

/* ========== 库存管理：导入/导出纯文字、等内边距、内容居中 ========== */
.main-content .inv-mgmt-excel-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.main-content .inv-mgmt-excel-actions .inv-mgmt-upload {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
}

.main-content .inv-mgmt-excel-btn.el-button {
  margin: 0 !important;
  padding: 12px !important;
  min-width: 92px;
  box-sizing: border-box;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: auto !important;
  min-height: 36px;
  line-height: 1.4 !important;
}

.main-content .inv-mgmt-excel-btn.el-button span {
  margin: 0 !important;
}

.main-content .inv-mgmt-template-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-height: 36px;
  padding: 8px 14px;
  margin: 0;
  color: var(--primary-color);
  font-size: 14px;
  text-decoration: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  white-space: nowrap;
}

.main-content .inv-mgmt-template-link:hover {
  text-decoration: underline;
}

.main-content .el-table td.inv-col-nowrap .cell {
  white-space: nowrap;
}

.account-info-body {
  font-family: var(--app-font);
  color: #1e293b;
}

.account-dialog-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;
}

.account-dialog-title-main {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #0f172a;
  font-family: var(--app-font);
}

.account-dialog-title-sub {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
}

.account-hero {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 16px 18px;
  margin: 0 0 18px;
  border-radius: 16px;
  background:
    radial-gradient(120% 140% at 0% 0%, rgba(43, 182, 115, 0.18), transparent 55%),
    linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #134e3a 100%);
  color: #fff;
}

.account-avatar-upload {
  flex-shrink: 0;
}

.account-avatar-upload .el-upload {
  display: block;
}

.account-avatar-ring {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
  background: linear-gradient(135deg, #2bb673, #0f172a);
  cursor: pointer;
}

.account-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.account-avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 800;
  color: #fff;
}

.account-avatar-mask {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
}

.account-hero-meta {
  min-width: 0;
  flex: 1;
}

.account-hero-name {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.01em;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-hero-role {
  margin-top: 4px;
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: rgba(43, 182, 115, 0.22);
  border: 1px solid rgba(74, 222, 128, 0.35);
  color: #bbf7d0;
}

.account-hero-hint {
  margin-top: 10px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

.account-field {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 10px 14px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.account-field--block {
  align-items: start;
}

.account-field--readonly .account-field-value {
  color: #334155;
  font-weight: 600;
}

.account-field-label {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.account-field-value {
  min-width: 0;
  word-break: break-all;
  font-size: 14px;
  line-height: 1.5;
}

.account-field-control {
  min-width: 0;
}

.account-info-perms {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.account-info-tag {
  margin: 0 !important;
  border-color: rgba(43, 182, 115, 0.28) !important;
  color: #166534 !important;
  background: rgba(43, 182, 115, 0.08) !important;
}

.account-dialog-footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.account-info-dialog .el-dialog__header {
  padding: 18px 22px 10px;
}

.account-info-dialog .el-dialog__body {
  padding: 8px 22px 6px;
}

.account-info-dialog .el-dialog__footer {
  padding: 12px 22px 18px;
}

.notification-item {
  cursor: pointer;
  border-radius: 8px;
  padding: 6px 8px;
}

.notification-item:hover {
  background: #f1f5f9;
}

.notification-panel {
  max-height: 360px;
  overflow-y: auto;
}

.notification-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.notification-panel-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  font-family: var(--app-font);
}

.notification-mark-all {
  padding: 0;
}

.notification-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.notification-item.is-delivery-done {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.notification-item.is-review {
  background: #fffbeb;
  border-color: #fde68a;
}

.notification-item.is-info {
  background: #f8fafc;
}

.notification-item-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  background: #e2e8f0;
  color: #475569;
}

.notification-item.is-delivery-done .notification-item-icon {
  background: #10b981;
  color: #fff;
}

.notification-item-body {
  flex: 1;
  min-width: 0;
}

.notification-item-title {
  font-size: 13px;
  font-weight: 700;
  color: #303133;
  line-height: 1.4;
}

.notification-item-content {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  word-break: break-all;
}

.notification-empty {
  font-size: 12px;
  color: #909399;
  text-align: center;
  padding: 16px 0;
}

.cashier-desk-root {
  height: 100vh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #eef2f7;
  overflow: hidden;
}

.cashier-desk-root > .cashier-page {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.cashier-desk-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 22px;
  background: linear-gradient(90deg, #0c1118, #141b24);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.cashier-desk-expire {
  flex: 1;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
  font-size: 13px;
}

.cashier-desk-exit {
  color: #fff !important;
}
</style>